import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Wrench, X } from "lucide-react";
import { getMaintenanceBanner } from "../services/maintenance";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
  });
};

/**
 * Platform-wide planned-maintenance notice.
 *
 * Reads a single window from the server rather than deciding between several client-side — two
 * banners stacked is worse than the more urgent one alone, and web and mobile picking differently
 * would be worse still.
 *
 * Dismissal is per-window and deliberately session-scoped (`sessionStorage`): someone who closes it
 * should not be re-nagged on every route change, but should see it again next time they sign in,
 * because the outage has not gone away. A window already in progress cannot be dismissed at all —
 * that one is explaining why things are broken right now.
 */
function MaintenanceBanner() {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getMaintenanceBanner();
      if (!res?.hasMaintenance) return setBanner(null);
      setBanner(res);
      setDismissed(sessionStorage.getItem(`maint_dismissed_${res.maintenanceId}`) === "1");
    } catch {
      // Never let a banner lookup break the page it sits above.
      setBanner(null);
    }
  }, []);

  useEffect(() => {
    load();
    // Picks up a window starting, or being cancelled, without a reload.
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [load]);

  if (!banner) return null;
  const live = !!banner.isInProgress;
  if (dismissed && !live) return null;

  const onDismiss = () => {
    sessionStorage.setItem(`maint_dismissed_${banner.maintenanceId}`, "1");
    setDismissed(true);
  };

  return (
    <div
      className={`w-full px-4 py-2.5 text-sm ${
        live ? "bg-amber-500 text-white" : "bg-slate-800 text-slate-100"
      }`}
      role="status"
    >
      <div className="mx-auto flex max-w-[1536px] items-start gap-2">
        {live ? <AlertTriangle size={17} className="mt-0.5 shrink-0" /> : <Wrench size={16} className="mt-0.5 shrink-0" />}
        <div className="flex-1">
          <span className="font-semibold">
            {live ? "Maintenance in progress" : banner.title}
          </span>
          <span className="ml-2 opacity-90">
            {live
              ? `Expected back by ${fmt(banner.endsAt)}. ${banner.message?.split("\n")[0] || ""}`
              : `${fmt(banner.startsAt)} – ${fmt(banner.endsAt)}. ${banner.message?.split("\n")[0] || ""}`}
          </span>
        </div>
        {/* A window that is happening right now is not dismissable — it is the explanation for
            whatever the person is about to hit. */}
        {!live && (
          <button onClick={onDismiss} className="shrink-0 opacity-70 hover:opacity-100" aria-label="Dismiss">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default MaintenanceBanner;
